<!-- StartAnimation.vue -->
<template>
  <div v-if="visible" class="start-animation-overlay" @animationend="onFadeOutEnd">
    <div class="animation-container">
      <!-- 光环环 -->
      <div class="ring ring-1"></div>
      <div class="ring ring-2"></div>
      <div class="ring ring-3"></div>
      
      <!-- 中心Logo -->
      <div class="center-logo">
        <span class="logo-text">{{ logoText }}</span>
      </div>
      
      <!-- 粒子 -->
      <div class="particles">
        <div 
          v-for="i in particleCount" 
          :key="i" 
          class="particle" 
          :style="getParticleStyle(i)"
        ></div>
      </div>
      
      <!-- 扫描线 -->
      <div class="scan-line"></div>
    </div>
    
    <!-- 底部文字 -->
    <div class="start-text">{{ bottomText }}</div>
  </div>
</template>

<script>
export default {
    
  name: 'StartAnimation',
  
  props: {
    // 是否显示
    value: {
      type: Boolean,
      default: false
    },
    // 中心Logo文字
    logoText: {
      type: String,
      default: 'GO'
    },
    // 底部文字
    bottomText: {
      type: String,
      default: 'GAME START!'
    },
    // 粒子数量
    particleCount: {
      type: Number,
      default: 24
    },
    // 自动隐藏延迟(ms)，0表示不自动隐藏
    autoHideDelay: {
      type: Number,
      default: 2500
    },
    // 是否启用渐出效果
    fadeOut: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      visible: this.value,
      isFadingOut: false,
      timer: null
    };
  },
  
  watch: {
    value(newVal) {
      if (newVal) {
        this.show();
      } else {
        this.hide();
      }
    }
  },
  
  beforeDestroy() {
    this.clearTimer();
  },
  
  methods: {
    /**
     * 显示动效
     */
    show() {
      this.visible = true;
      this.isFadingOut = false;
      this.clearTimer();
      
      // 自动隐藏
      if (this.autoHideDelay > 0) {
        this.timer = setTimeout(() => {
          this.hide();
        }, this.autoHideDelay);
      }
      
      this.$emit('show');
    },
    
    /**
     * 隐藏动效
     */
    hide() {
      if (this.fadeOut && this.visible) {
        this.isFadingOut = true;
        this.clearTimer();
        // 等待渐出动画结束
        setTimeout(() => {
          this.visible = false;
          this.isFadingOut = false;
          this.$emit('hide');
          this.$emit('input', false);
        }, 600);
      } else {
        this.visible = false;
        this.isFadingOut = false;
        this.clearTimer();
        this.$emit('hide');
        this.$emit('input', false);
      }
    },
    
    /**
     * 切换显示状态
     */
    toggle() {
      if (this.visible) {
        this.hide();
      } else {
        this.show();
      }
    },
    
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    
    onFadeOutEnd() {
      if (this.isFadingOut) {
        this.visible = false;
        this.isFadingOut = false;
        this.$emit('hide');
        this.$emit('input', false);
      }
    },
    
    /**
     * 获取粒子样式
     */
    getParticleStyle(index) {
      const colors = [
        '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', 
        '#ff6bb5', '#00d2ff', '#ff9f43', '#a29bfe',
        '#fd79a8', '#00cec9', '#fdcb6e', '#e17055'
      ];
      
      const angle = (index / this.particleCount) * 360 + (Math.random() - 0.5) * 20;
      const distance = 60 + Math.random() * 140;
      const size = 3 + Math.random() * 10;
      const delay = Math.random() * 1.8;
      const duration = 1.2 + Math.random() * 1.8;
      const color = colors[index % colors.length];
      
      return {
        '--angle': angle + 'deg',
        '--distance': distance + 'px',
        '--size': size + 'px',
        '--delay': delay + 's',
        '--duration': duration + 's',
        '--color': color,
        background: color,
        boxShadow: `0 0 ${size * 2}px ${color}40`
      };
    }
  }
};
</script>

<style scoped>
/* ========== 启动动效样式 ========== */
.start-animation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  background: radial-gradient(ellipse at center, #0a0a2a 0%, #000000 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: fadeInOverlay 0.5s ease-out;
}

.start-animation-overlay.fade-out {
  animation: fadeOutOverlay 0.6s ease-in forwards;
}

@keyframes fadeInOverlay {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOutOverlay {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.05);
  }
}

.animation-container {
  position: relative;
  width: 300px;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ===== 光环环 ===== */
.ring {
  position: absolute;
  border-radius: 50%;
  border: 3px solid transparent;
  animation: ringPulse 2s ease-in-out infinite;
}

.ring-1 {
  width: 280px;
  height: 280px;
  border-top-color: #ff6b6b;
  border-right-color: #ffd93d;
  border-bottom-color: #6bcb77;
  border-left-color: #4d96ff;
  animation-delay: 0s;
}

.ring-2 {
  width: 220px;
  height: 220px;
  border-top-color: #ff6bb5;
  border-right-color: #00d2ff;
  border-bottom-color: #ffd93d;
  border-left-color: #ff6b6b;
  animation-delay: 0.3s;
  animation-direction: reverse;
}

.ring-3 {
  width: 160px;
  height: 160px;
  border-top-color: #00d2ff;
  border-right-color: #ff6b6b;
  border-bottom-color: #ff6bb5;
  border-left-color: #6bcb77;
  animation-delay: 0.6s;
}

@keyframes ringPulse {
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: rotate(180deg) scale(1.1);
    opacity: 1;
  }
  100% {
    transform: rotate(360deg) scale(1);
    opacity: 0.8;
  }
}

/* ===== 中心Logo ===== */
.center-logo {
  position: relative;
  z-index: 2;
  width: 100px;
  height: 100px;
  background: radial-gradient(circle, #ffd93d 0%, #ff6b6b 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: logoPulse 1.5s ease-in-out infinite;
  box-shadow: 
    0 0 60px rgba(255, 107, 107, 0.5),
    0 0 120px rgba(255, 217, 61, 0.3);
}

@keyframes logoPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 
      0 0 60px rgba(255, 107, 107, 0.5),
      0 0 120px rgba(255, 217, 61, 0.3);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 
      0 0 80px rgba(255, 107, 107, 0.7),
      0 0 160px rgba(255, 217, 61, 0.5);
  }
}

.logo-text {
  font-size: 48px;
  font-weight: 900;
  color: #fff;
  font-family: 'Arial Black', 'Impact', sans-serif;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  animation: textGlow 1.5s ease-in-out infinite alternate;
}

@keyframes textGlow {
  from {
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }
  to {
    text-shadow: 
      0 0 40px rgba(255, 255, 255, 0.8),
      0 0 80px rgba(255, 217, 61, 0.4);
  }
}

/* ===== 粒子 ===== */
.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.particle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size, 6px);
  height: var(--size, 6px);
  border-radius: 50%;
  background: var(--color, #fff);
  box-shadow: 0 0 calc(var(--size, 6px) * 2) var(--color, #fff);
  animation: particleFly var(--duration, 2s) ease-out infinite;
  animation-delay: var(--delay, 0s);
  opacity: 0;
}

@keyframes particleFly {
  0% {
    transform: translate(-50%, -50%) rotate(0deg) translateX(0) scale(1);
    opacity: 1;
  }
  100% {
    transform: 
      translate(-50%, -50%) 
      rotate(calc(var(--angle, 0deg) * 2)) 
      translateX(var(--distance, 100px)) 
      scale(0);
    opacity: 0;
  }
}

/* ===== 扫描线 ===== */
.scan-line {
  position: absolute;
  top: -50%;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(77, 150, 255, 0.8), transparent);
  animation: scanMove 2s linear infinite;
  z-index: 3;
  box-shadow: 0 0 20px rgba(77, 150, 255, 0.5);
}

@keyframes scanMove {
  0% {
    top: -50%;
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    top: 150%;
    opacity: 0;
  }
}

/* ===== 底部文字 ===== */
.start-text {
  margin-top: 40px;
  font-size: 60px;
  font-weight: 900;
  font-family: 'Arial Black', 'Impact', sans-serif;
  color: #fff;
  letter-spacing: 8px;
  background: linear-gradient(90deg, #ff6b6b, #ffd93d, #6bcb77, #4d96ff, #ff6bb5);
  background-size: 300% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 2s ease-in-out infinite;
  filter: drop-shadow(0 0 30px rgba(255, 107, 107, 0.3));
}

@keyframes gradientMove {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .animation-container {
    width: 200px;
    height: 200px;
  }
  
  .ring-1 {
    width: 180px;
    height: 180px;
  }
  
  .ring-2 {
    width: 140px;
    height: 140px;
  }
  
  .ring-3 {
    width: 100px;
    height: 100px;
  }
  
  .center-logo {
    width: 60px;
    height: 60px;
  }
  
  .logo-text {
    font-size: 28px;
  }
  
  .start-text {
    font-size: 32px;
    margin-top: 30px;
    letter-spacing: 4px;
  }
}

@media (max-width: 480px) {
  .animation-container {
    width: 150px;
    height: 150px;
  }
  
  .ring-1 {
    width: 130px;
    height: 130px;
    border-width: 2px;
  }
  
  .ring-2 {
    width: 100px;
    height: 100px;
    border-width: 2px;
  }
  
  .ring-3 {
    width: 70px;
    height: 70px;
    border-width: 2px;
  }
  
  .center-logo {
    width: 45px;
    height: 45px;
  }
  
  .logo-text {
    font-size: 20px;
  }
  
  .start-text {
    font-size: 24px;
    margin-top: 20px;
    letter-spacing: 2px;
  }
}
</style>